import React, { useState, useEffect, useRef } from "react";
import { useLanguage } from "../context/LanguageContext";
import logolycs from "../assets/images/png/logoLycs.png";
import logoluxeevents from "../assets/images/png/luxe-events.jpg";

function TimelineItem({ exp, index }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="timeline-item"
      style={{
        opacity: visible ? 1 : 0,
        transform: `translateY(${visible ? 0 : 30}px)`,
        transition: `all 0.6s cubic-bezier(0.16,1,0.3,1) ${index * 120}ms`,
      }}
    >
      <div className="timeline-dot" />
      <div className="timeline-card">
        <div className="timeline-header">
          {exp.logo && (
            <img src={exp.logo} alt={exp.company} className="timeline-logo" />
          )}
          <div>
            <div className="timeline-company">{exp.company}</div>
            {exp.location && (
              <div style={{ fontSize: "0.78rem", color: "var(--text-secondary)", marginTop: 2 }}>
                {exp.location}
              </div>
            )}
            <div className="timeline-date">{exp.period}</div>
          </div>
        </div>
        {exp.description && (
          <p style={{ fontSize: "0.85rem", color: "var(--text-secondary)", marginBottom: 12, lineHeight: 1.6, fontStyle: "italic" }}>
            {exp.description}
          </p>
        )}
        {exp.tasks && exp.tasks.length > 0 && (
          <ul className="timeline-tasks" style={{ marginBottom: exp.projects?.length ? 12 : 0 }}>
            {exp.tasks.map((task, ti) => (
              <li key={ti}>{task}</li>
            ))}
          </ul>
        )}
        {exp.projects && exp.projects.map((proj, idx) => (
          <div key={idx} className="timeline-project">
            {proj.name && <div className="timeline-project-name">{proj.name}</div>}
            <div className="timeline-project-date">{proj.date}</div>
            <ul className="timeline-tasks">
              {proj.tasks.map((task, ti) => (
                <li key={ti}>{task}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function Experience() {
  const { t, lang } = useLanguage();
  const headerRef = useRef(null);
  const [headerVisible, setHeaderVisible] = useState(false);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setHeaderVisible(true); obs.disconnect(); } },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const experiences = lang === "fr" ? [
    {
      company: "Lycs SAS",
      logo: logolycs,
      location: "Immeuble Malakaï, Route de Ngor, Dakar",
      period: `2023 – ${t("expPresent")}`,
      description:
        "Consultant technique sur des projets internationaux à forte composante d'intégration. Intervention en tant que développeur Salesforce (Apex, LWC), développeur MuleSoft, développeur API Spring et développeur mobile Flutter.",
      tasks: [
        "Conception, configuration et déploiement de flux MuleSoft pour des clients grands comptes",
        "Développement de composants Lightning Web Components (LWC) et classes Apex sur Salesforce",
        "Création de templates email et CloudPages sur Salesforce Marketing Cloud",
        "Développement d'APIs avec Spring Boot (Java/J2EE) pour des services back-end",
        "Conception de solutions IA avec Agentforce intégrées à WhatsApp et Slack",
        "Développement d'applications mobiles multiplateformes avec Flutter",
        "Lead technique sur le produit Clyx : architecture, base de données distribuée, déploiement",
      ],
    },
    {
      company: "Nguélémou Production (Luxe Events Sénégal)",
      logo: logoluxeevents,
      location: "Touba Ndamé & Dakar Parcelles Assainies U26",
      period: "2019 – 2020",
      description: "Gérant - Gestion opérationnelle et commerciale de la structure.",
      tasks: [
        "Gestion opérationnelle et commerciale de la structure",
      ],
    },
    {
      company: "Cours privés franco-arabe Cheikh Fallou Mbacké",
      logo: null,
      location: "Yeumbeul, Route de Boune",
      period: "2019",
      description: "Intervenant - Enseignement et encadrement pédagogique.",
      tasks: [
        "Enseignement et encadrement pédagogique",
      ],
    },
  ] : [
    {
      company: "Lycs SAS",
      logo: logolycs,
      location: "Immeuble Malakaï, Route de Ngor, Dakar",
      period: `2023 - ${t("expPresent")}`,
      description:
        "Technical consultant on international projects with strong integration components. Working as Salesforce developer (Apex, LWC), MuleSoft developer, Spring API developer and Flutter mobile developer.",
      tasks: [
        "Design, configuration and deployment of MuleSoft flows for enterprise clients",
        "Development of Lightning Web Components (LWC) and Apex classes on Salesforce",
        "Creation of email templates and CloudPages on Salesforce Marketing Cloud",
        "API development with Spring Boot (Java/J2EE) for back-end services",
        "Design of AI solutions with Agentforce integrated with WhatsApp and Slack",
        "Cross-platform mobile app development with Flutter",
        "Technical lead on the Clyx product: architecture, distributed database, deployment",
      ],
    },
    {
      company: "Nguélémou Production (Luxe Events Senegal)",
      logo: logoluxeevents,
      location: "Touba Ndamé & Dakar Parcelles Assainies U26",
      period: "2019 – 2020",
      description: "Manager - Operational and commercial management of the company.",
      tasks: [
        "Operational and commercial management of the company",
      ],
    },
    {
      company: "Private Franco-Arab School Cheikh Fallou Mbacké",
      logo: null,
      location: "Yeumbeul, Route de Boune",
      period: "2019",
      description: "Instructor - Teaching and pedagogical supervision.",
      tasks: [
        "Teaching and pedagogical supervision",
      ],
    },
  ];

  return (
    <section className="experience-section" id="experience">
      <div className="experience-content">
        <div
          ref={headerRef}
          style={{
            textAlign: "center",
            marginBottom: 16,
            opacity: headerVisible ? 1 : 0,
            transform: `translateY(${headerVisible ? 0 : -20}px)`,
            transition: "all 0.8s cubic-bezier(0.16,1,0.3,1)",
          }}
        >
          <p className="section-eyebrow">{t("expEyebrow")}</p>
          <h2 className="section-title">{t("expTitle")}<span className="dot">.</span></h2>
          <p className="section-subtitle">{t("expSubtitle")}</p>
          <div className="section-underline" style={{ width: headerVisible ? 80 : 0, margin: "14px auto 0" }} />
        </div>

        <div className="timeline">
          {experiences.map((exp, i) => (
            <TimelineItem key={i} exp={exp} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default Experience;
